import graphql from "babel-plugin-relay/macro";
import { ListBox } from "primereact/listbox";
import { useEffect, useMemo, useState } from "react";
import { useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { TagSelectionField_Query } from "../../../__generated__/TagSelectionField_Query.graphql";
import { TagSelectionField_QueryFragment$key } from "../../../__generated__/TagSelectionField_QueryFragment.graphql";
import { TagSelectionField_Refetch } from "../../../__generated__/TagSelectionField_Refetch.graphql";
import { RenderConfig } from "../core/form/ValidatedField";

const QUERY = graphql`
	query TagSelectionField_Query(
		$tagIds: [ID!]!
		$first: Int!
		$skip: Boolean!
		$filterByName: String
		$after: String
	) {
		nodes(ids: $tagIds) @skip(if: $skip) {
			... on Tag {
				id
				name
				colorHex
			}
		}
		...TagSelectionField_QueryFragment
			@arguments(filterByName: $filterByName, first: $first, after: $after)
	}
`;

const QUERY_FRAGMENT = graphql`
	fragment TagSelectionField_QueryFragment on Query
	@refetchable(queryName: "TagSelectionField_Refetch")
	@argumentDefinitions(
		first: { type: "Int" }
		after: { type: "String" }
		filterByName: { type: "String" }
	) {
		Admin {
			Tag {
				Tags(first: $first, after: $after, filterByName: $filterByName)
					@connection(key: "TagSelectionField_Tags") {
					pageInfo {
						endCursor
						hasPreviousPage
						hasNextPage
						startCursor
					}
					edges {
						node {
							id
							name
							colorHex
						}
					}
				}
			}
		}
	}
`;

export const TagSelectionField = ({
	fieldValue,
	updateField,
	onChange,
}: RenderConfig<string[]>) => {
	const [filter, setFilter] = useState("");

	const query = useLazyLoadQuery<TagSelectionField_Query>(
		QUERY,
		{
			tagIds: fieldValue || [],
			skip: !fieldValue || fieldValue.length === 0,
			filterByName: undefined,
			first: 20,
			after: null,
		},
		{
			fetchPolicy: "store-or-network",
		},
	);
	useEffect(() => {
		refetch({ filterByName: filter });
	}, [filter]);

	const {
		data: {
			Admin: {
				Tag: {
					Tags: { edges: tags },
				},
			},
		},
		refetch,
	} = usePaginationFragment<TagSelectionField_Refetch, TagSelectionField_QueryFragment$key>(
		QUERY_FRAGMENT,
		query,
	);

	const options = useMemo(() => {
		const allNodes = [
			...(query.nodes || []),
			...(tags?.map((e) => e?.node).filter(Boolean) || []),
		];
		const map = new Map(allNodes.map((item: any) => [item.id, item]));

		return Array.from(map.values())
			.filter(Boolean)
			.sort((x: any, y: any) => (x.name || "").localeCompare(y.name || ""))
			.map((n: any) => ({
				label: n.name,
				value: n.id,
			}));
	}, [query.nodes, tags]);

	return (
		<ListBox
			value={fieldValue}
			options={options}
			onChange={(e) => {
				updateField(e.value);
				onChange?.();
			}}
			multiple
			filter
			filterValue={filter}
			onFilterValueChange={(e) => setFilter(e.value)}
			listStyle={{ maxHeight: "250px" }}
		/>
	);
};
